import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";

// Components
import MicrositeNavbar from "../../components/microsite/MicrositeNavbar";
import MicroHeroSection from "../../components/microsite/MicrositeHeroSection";
import MicrositeAbout from "../../components/microsite/MicrositeAbout";
import MicrositeServicesSection from "../../components/microsite/MicrositeServiceSection";
import MicrositeTestimonialPreview from "../../components/microsite/MicrositeTestomonialPreview";
import MicrositeContactUs from "../../components/microsite/MicrositeContactUs";
import MicrositeGallerySection from "../../components/microsite/MicrositeGallerySection";

// TO SHOW WE HAVE MISSING ANY INFO OF ANY ON SECTION
import IncompleteSection from "../../components/InCompleteSection";
//Page Transition
import PageTransition from "../../components/PageTransition";

// Contexts + Hooks
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useSectionContext } from "../../context/SectionContext";

// Types
import { Testimonial } from "../../types/testimonial";
import { Service } from "../../types/service";
import { HeroData } from "../../types/herosection";
import { getWebsiteSettings, WebsiteSettingsResponse } from "../../services/websitesetting";
import GlobalLoader from "../../components/ui/GlobalLoader";

const Microsite: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { sections: activeSections } = useSectionContext();

  const [activeLink, setActiveLink] = useState("home");
  const [services, setServices] = useState<Service[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [businessInfo, setBusinessInfo] = useState<{ description: string }>({
    description: "",
  });
  const [loading, setLoading] = useState(true);
  const [siteData, setSiteData] = useState<WebsiteSettingsResponse | null>(null);

  useEffect(() => {
    const loadSiteData = async () => {
      setLoading(true);
      try {
        const data = await getWebsiteSettings();
        setSiteData(data);

        // Map data to states
        if (data.about_section) {
          setBusinessInfo({ description: data.about_section.description });
        } else if (data.about_us_page) {
          setBusinessInfo({ description: data.about_us_page.description });
        }

        // Map services and testimonials if they are available in the response
        // Note: they might not be in WebsiteSettingsResponse, so check other sources or assume empty for now
        // if (data.services) setServices(data.services);
        // if (data.testimonials) setTestimonials(data.testimonials);
      } catch (err) {
        console.error("Failed to load microsite data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadSiteData();
  }, [userId]);

  // Derived Hero Data for Components
  const heroData = useMemo<HeroData>(() => {
    if (!siteData) return { title: "", tagline: "", ctaText: "", coverImage: "", logo: "", themeColor: "#885ABB" };
    // Assuming hero info might be in theme_settings or a sub-object
    const theme = siteData.website_theme_settings;
    const hero = (siteData as any).hero_section; // Fallback or assume it exists in response

    return {
      title: hero?.title || theme?.title || "",
      tagline: hero?.tagline || "",
      ctaText: hero?.cta_text || "Book Appointment",
      coverImage: hero?.cover_image ? (hero.cover_image.startsWith('http') ? hero.cover_image : `https://salonify.co.uk/storage/${hero.cover_image}`) : "",
      logo: theme?.logo ? (theme.logo.startsWith('http') ? theme.logo : `https://salonify.co.uk/storage/${theme.logo}`) : "",
      themeColor: theme?.primary_color || "#885ABB",
    };
  }, [siteData]);

  const galleryImages = useMemo(() => {
    return siteData?.galleries?.map(g => g.image.startsWith('http') ? g.image : `https://salonify.co.uk/storage/${g.image}`) || [];
  }, [siteData]);

  const contactDetails = useMemo(() => {
    if (!siteData?.footer_setting) return { contactEmail: "", facebook: "", instagram: "", pinterest: "", linkedin: "", twitter: "", copyrights: "" };
    const f = siteData.footer_setting;
    const socialMap: any = {};
    if (Array.isArray(f.social_links)) {
      f.social_links.forEach((s: any) => socialMap[s.name.toLowerCase()] = s.link);
    }
    return {
      contactEmail: f.contact_email,
      facebook: socialMap.facebook || "",
      instagram: socialMap.instagram || "",
      twitter: socialMap.twitter || "",
      linkedin: socialMap.linkedin || "",
      pinterest: socialMap.pinterest || "",
      copyrights: "hardcoded",
    };
  }, [siteData]);

  // ------------------------- SCROLLING LOGIC -------------------------
  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");

    const handleScroll = () => {
      let current = "home";
      sections.forEach((section) => {
        const sectionEl = section as HTMLElement;
        const sectionTop = sectionEl.offsetTop - 150;
        if (window.scrollY >= sectionTop) {
          current = sectionEl.getAttribute("id") || "home";
        }
      });
      setActiveLink(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ------------------------- SMOOTH SCROLL -------------------------
  useEffect(() => {
    const links = document.querySelectorAll("a[href^='#']");

    const smoothScroll = (e: Event) => {
      e.preventDefault();
      const targetId = (e.currentTarget as HTMLAnchorElement)
        .getAttribute("href")
        ?.substring(1);
      const targetEl = targetId ? document.getElementById(targetId) : document.body;

      if (targetEl) {
        window.scrollTo({
          top: targetEl.offsetTop - 100,
          behavior: "smooth",
        });
      }
    };

    links.forEach((link) => link.addEventListener("click", smoothScroll));
    return () => links.forEach((link) => link.removeEventListener("click", smoothScroll));
  }, []);

  // ------------------------- MEMOIZED DATA -------------------------
  const aboutData = useMemo(
    () => ({
      title: `About ${heroData.title}`,
      description:
        businessInfo.description
    }),
    [businessInfo.description]
  );

  // ------------------------- COMPLETION LOGIC -------------------------
  const isHeroComplete = !!(
    heroData.title && heroData.tagline && heroData.coverImage && heroData.ctaText
  );
  // const isAboutComplete = businessInfo.description.trim().length > 0;
  const isServiceComplete = services.length > 0;
  const isTestimonialComplete = testimonials.length > 0;
  const isGalleryComplete = galleryImages.length > 0;

  // const isContactComplete =

  //   contactDetails.email.trim().length>0 &&
  //   contactDetails.instagram.trim() &&
  //   contactDetails.facebook.trim()&&
  //   contactDetails.copyrights.trim()&&
  //   contactDetails.twitter.trim()&&
  //   contactDetails.linkedin.trim()&&
  //   contactDetails.pinterest.trim();


  const isAboutComplete = (businessInfo.description || "").trim().length > 0;
  console.log("---------------------------------", contactDetails);
  const isContactComplete =
    (contactDetails.contactEmail || "").trim() &&
    (contactDetails.instagram || "").trim() &&
    (contactDetails.facebook || "").trim() &&
    (contactDetails.copyrights || "").trim() &&
    (contactDetails.twitter || "").trim() &&
    (contactDetails.linkedin || "").trim() &&
    (contactDetails.pinterest || "").trim();

  // ------------------------- RENDERING -------------------------
  const renderIncomplete = useCallback(
    (title: string, path: string) => <IncompleteSection title={title} path={path} />,
    []
  );

  return (
    <PageTransition>
      <div className="w-full bg-white text-gray-900 scroll-smooth">
        {loading && <GlobalLoader initialPageLoad={true} description="Building your website preview..." />}
        {/* NAVBAR */}
        <MicrositeNavbar
          hero={heroData}
          activeLink={activeLink}
          themeColor={heroData.themeColor}
        />

        {/* HERO SECTION */}
        {activeSections.herosection === "On" && (
          <section
            id="home"
          >
            {isHeroComplete ? (
              <MicroHeroSection hero={heroData} themeColor={heroData.themeColor} />
            ) : (
              renderIncomplete("Hero", "/website/herosection")
            )}
          </section>
        )}

        <section
          id="about"
        >
          {/* ABOUT SECTION */}
          {isAboutComplete ? (
            <MicrositeAbout
              about={aboutData}
              themeColor={heroData.themeColor}
              logo={heroData.logo}
            />
          ) : (
            renderIncomplete("About", "/website/storesetting")
          )}
        </section>

        {/* SERVICES SECTION */}
        {activeSections.servicesection === "On" && (
          <section id="services">
            {isServiceComplete ? (
              <MicrositeServicesSection
                themeColor={heroData.themeColor}
                services={services}
              />
            ) : (
              renderIncomplete("Service", "/website/servicesection")
            )}
          </section>
        )}



        {/* TESTIMONIAL SECTION */}
        {activeSections.testimonialsection === "On" && (
          <section id="testimonial">
            {isTestimonialComplete ? (
              <MicrositeTestimonialPreview
                testimonials={testimonials}
                color={heroData.themeColor}
              />
            ) : (
              renderIncomplete("Testimonial", "/website/testimonialsection")
            )}
          </section>
        )}

        {activeSections.gallerysection === "On" && (
          <section id="gallery">
            {isGalleryComplete ? (
              <MicrositeGallerySection
                images={galleryImages}
                themeColor={heroData.themeColor}
              />
            ) : (
              renderIncomplete("Gallery", "/website/gallerysection")
            )}
          </section>
        )}


        {/* CONTACT + FOOTER */}

        <>
          <section id="contact">
            <MicrositeContactUs
              themeColor={heroData.themeColor}
              contactDetails={contactDetails}
            />
          </section>
          {/* <MicrositeFooter
      themeColor={heroData.themeColor}

    /> */}
        </>
        {/* ) : (
  renderIncomplete("Contact Section", "/website/footersection")
)} */}

      </div>
    </PageTransition>
  );
};

export default Microsite;
